import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useCookies } from 'react-cookie';
import { useDispatch, useSelector } from 'react-redux';

import _isEmpty from 'lodash/isEmpty';

import { getConsumerMetadata } from '@atc/consumer-metadata';
import { useFeatures } from '@atc/features';
import { getFirstPartyVistorId, getPixallId } from '@atc/pixall-fns';

import {
    SideBarToModal,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { ownerEmailSent } from 'reaxl-analytics-handlers';
import { EmailContext, EmailForm, sendEmailAndSaveDeal } from 'reaxl-email';

import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';

/**
 * Modal component that displays the additional inventory email modal.

 * @param {Object} props - Component props
 * @param {Object} props.dealsDuck - Duck object for managing deal state
 * @param {Object} props.dealToSubmit - Deal object that was submitted before opening the additional inventory modal
 * @param {string} props.env - Environment variable
 * @param {Function} props.fetcher - THe fetcher to send the email
 * @param {boolean} props.isXs - Is the screen size xs
 * @param {Object} props.listing - Current vehicle listing object
 * @param {Object} props.nextStepsProps - Props containing next steps after submitting a lead
 * @param {Function} props.nextStepsProps.getAdditionalInventory - Function to fetch additional inventory
 * @param {Function} props.nextStepsProps.SimilarListingsFetcher - Function to fetch similar listings
 * @param {React.ReactNode} props.nextStepsProps.nextSteps - Next steps component
 * @param {Object} props.paymentsDuck - Duck object for managing payments
 * @param {boolean} props.show - Flag to show/hide the modal
 * @param {Object} props.userDuck - Duck containing user information
 * @returns {React.ReactElement} The AdditionalInventoryModal component
 */
function AdditionalInventoryModal({
    dealsDuck,
    dealToSubmit,
    env,
    fetcher,
    isXs,
    listing,
    nextStepsProps,
    paymentsDuck,
    show,
    userDuck,
}) {

    const {
        brand: [, {
            leads_cc_flag: copySelf,
            leads_partner_name: partnerName,
            channel,
        }],
        enable_similar_listings_in_additional_inventory: [isSimilarListingsEnabled],
        nds_calc: [ndsEnabled],

    } = useFeatures([
        'brand',
        'nds_calc',
        'enable_similar_listings_in_additional_inventory',
    ]);

    const dispatch = useDispatch();

    const pixallFirstPartyId = getFirstPartyVistorId();
    const pixallThirdPartyId = getPixallId() || 'nl';

    const [cookies = {}] = useCookies(['ATC_ID', 'pxa_ipv4']);

    const { SimilarListingsFetcher } = nextStepsProps;
    const { pricingDetail } = listing;
    const userZip = useSelector((state) => userDuck?.selectors?.getZip(state));
    const paymentsData = useSelector(paymentsDuck.selectors.getDuckState);
    const payments = useMemo(() => paymentsData || {}, [paymentsData]);

    const {
        interactions: walletInteractions = {},
        downPaymentPercent,
    } = payments;

    const { paymentEmphasis, hasUpdatedWallet, hasUpdatedNDSCalc } = walletInteractions || {};

    const isUserInvolved = paymentEmphasis || hasUpdatedWallet || hasUpdatedNDSCalc;

    const [additionalInventoryInfo, setAdditionalInventoryInfo] = useState();
    const [selectedListings, setSelectedListings] = useState([]);
    const [showAlert, setShowAlert] = useState(true);

    const fetchInventory = async () => {
        if (show && nextStepsProps?.getAdditionalInventory) {
            let additionalInventoryData = await nextStepsProps.getAdditionalInventory();
            const additionalInventoryLength = additionalInventoryData?.listings?.length || 0;
            if (typeof SimilarListingsFetcher === 'function' && isSimilarListingsEnabled && userZip && additionalInventoryLength < 3) {
                let minPrice;
                let maxPrice;

                if (pricingDetail && (pricingDetail?.salePrice || pricingDetail?.incentive)) {
                    const salePrice = pricingDetail?.incentive || pricingDetail?.salePrice;
                    if (salePrice >= 30000) {
                        minPrice = Math.round(salePrice * 0.95);
                        maxPrice = Math.round(salePrice * 1.05);
                    } else {
                        minPrice = Math.round(salePrice * 0.90);
                        maxPrice = Math.round(salePrice * 1.10);
                    }
                }

                const searchQuery = {
                    bodyStyleCode: listing?.bodyStyleCodes?.[0] ?? listing?.bodyStyles?.[0]?.code,
                    channel: channel === 'ATC,PREVIEW' ? 'ATC' : channel,
                    classSeriesCode: listing?.classSeriesCode,
                    dealerId: null,
                    driveCode: listing?.driveGroup ?? '',
                    excludedListingId: listing?.id,
                    extColorSimple: listing?.exteriorColorSimple ?? '',
                    listingType: listing?.type?.toUpperCase() ?? '',
                    makeCode: listing?.makeCode ?? listing?.make?.code,
                    modelCode: listing.modelCode ?? listing?.model?.code,
                    minPrice,
                    maxPrice,
                    numRecords: 20,
                    ownerProduct: 'INVENTORY_CONQUEST',
                    searchRadius: 100,
                    startYear: listing?.year ? listing.year - 1 : '',
                    endYear: listing?.year ? listing.year + 1 : '',
                    transmissionCode: listing?.transmission?.name ?? '',
                    zip: userZip,
                };
                const similarListingsResponse = await SimilarListingsFetcher(searchQuery);

                const similarListings = similarListingsResponse?.listings;
                const filteredSimilarListings = similarListings ? similarListings?.slice(0, 3 - additionalInventoryLength) : [];

                if (!additionalInventoryData?.listings?.length) {
                    additionalInventoryData = {
                        listings: filteredSimilarListings,
                        hasSavedListings: false,
                    };
                } else if (additionalInventoryLength < 3) {
                    additionalInventoryData?.listings?.push(...filteredSimilarListings);
                }
            }

            setAdditionalInventoryInfo(additionalInventoryData ?? {});
        }

    };

    useEffect(() => {
        if (show) {
            fetchInventory();
            setSelectedListings([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    const handleListingSelect = (event, newSelection) => {
        const selections = [...selectedListings];
        const index = selections?.findIndex(((selection) => selection && (selection?.id === newSelection?.id)));
        if (index === -1) {
            selections.push(newSelection);
            sendClick('recentlyViewedVehicleNBAClick', event, {
                inventoryId: newSelection.id,
                hasSavedListings: additionalInventoryInfo?.hasSavedListings,
                isSimilarListingsEnabled,
            });
        } else {
            selections.splice(index, 1);
        }
        setSelectedListings(selections);
        setShowAlert(false);
    };

    const handleFormSubmit = useCallback(async (formData) => {
        const emailData = {
            cookies,
            copySelf,
            formData,
            partnerName,
            pixallFirstPartyId,
            pixallThirdPartyId,
            zip: userZip,
        };

        let consumerMetadata = {
            email: formData?.fromEmailAddress,
            phoneNumber: formData?.phoneNumber?.toString(),
        };

        try {
            const metaData = await getConsumerMetadata();
            if (!_isEmpty(metaData)) {
                consumerMetadata = metaData;
            }
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Failed to get consumer metadata');
        }

        selectedListings.forEach((selectedListing) => {
            let selectedListingCondition = selectedListing?.selectedListingType?.toLowerCase() ?? 'used';
            if (selectedListingCondition === 'certified') selectedListingCondition = 'used';

            const selectedListingRate = payments?.interestRate?.[selectedListingCondition].toString() ?? null;

            let selectedListingDownPayment = payments?.downPayment;
            if (payments?.downPayment === null) {
                selectedListingDownPayment = Math.floor(selectedListing.pricingDetail.salePrice * downPaymentPercent);
            }
            const additionalInventoryDeal = {
                ...dealToSubmit,
                dealType: 'finance',
                terms: payments?.loanTerm,
                rate: selectedListingRate,
                downPayment: selectedListingDownPayment,
            };

            delete additionalInventoryDeal.amountFinanced;
            delete additionalInventoryDeal.customPrice;
            delete additionalInventoryDeal.lender;
            delete additionalInventoryDeal.payment;
            delete additionalInventoryDeal.salePrice;
            delete additionalInventoryDeal.instantCashOffer;

            sendEmailAndSaveDeal({
                ...emailData,
                clickType: null,
                deal: additionalInventoryDeal,
                dealsDuck,
                dispatch,
                emailOwnerFetcher: fetcher,
                env,
                listing: {
                    id: selectedListing.id,
                    listingTitle: selectedListing.title,
                },
                originPage: 'vdp',
                owner: selectedListing?.owner,
                paymentsDuck,
            });

            sendClick(ownerEmailSent, null, {
                ...emailData,
                consumerMetadata,
                deal: additionalInventoryDeal,
                inventoryId: selectedListing.id,
                par: 'v_lstg',
                context: 'emailConfirmation',
                includePageId: false,
                ownerId: selectedListing?.owner?.id,
                isSimilarListingsEnabled,
                hasSavedListings: additionalInventoryInfo?.hasSavedListings,
            });
        });

        dispatch(vdpActiveInteractionDuck.creators.setKeys({
            showAdditionalInventoryModal: false,
            showEmailConfirmationModal: true,
        }));

    }, [
        cookies,
        copySelf,
        partnerName,
        pixallFirstPartyId,
        pixallThirdPartyId,
        userZip,
        selectedListings,
        payments,
        dealToSubmit,
        dealsDuck,
        dispatch,
        downPaymentPercent,
        fetcher,
        env,
        paymentsDuck,
        isSimilarListingsEnabled,
        additionalInventoryInfo,
    ]);

    const leadProfile = useSelector((state) => userDuck?.selectors?.getLeadProfile(state)) || {};

    const emailContextValue = useMemo(() => ({
        uiContext: 'multi',
    }), []);

    return (
        <SideBarToModal
            collapsed={isXs}
            title="Email Confirmation"
            show={show}
            modalOptions={{ bsSize: 'small' }}
            sideBarOptions={{
                fullWidth: true,
            }}
            content={(
                <EmailContext.Provider value={emailContextValue}>
                    <EmailForm
                        additionalInventoryInfo={additionalInventoryInfo}
                        nextSteps={nextStepsProps.nextSteps}
                        isConfirmation
                        showAlert={showAlert}
                        handleFormSubmit={handleFormSubmit}
                        userDuck={userDuck}
                        userProfile={leadProfile}
                        handleListingSelect={handleListingSelect}
                        isButtonDisabled={!selectedListings?.length}
                        ndsProps={{ walletOptIn: isUserInvolved && ndsEnabled }}
                    />
                </EmailContext.Provider>
            )}
            onHide={() => {
                dispatch(vdpActiveInteractionDuck.creators.setKeys({
                    showAdditionalInventoryModal: false,
                }));
            }}
        />
    );
}

export default AdditionalInventoryModal;
